import { useRef, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { testimonials } from "@/mocks/products";

export default function TestimonialsSection() {
  const { t, i18n } = useTranslation();
  const isFr = i18n.language === "fr";
  const sectionRef = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActive((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section ref={sectionRef} className="py-20 md:py-28 bg-white relative overflow-hidden">
      {/* Decorative */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-10 left-10 text-amber-100 text-9xl font-serif leading-none select-none">&ldquo;</div>
        <div className="absolute bottom-10 right-10 text-amber-100 text-9xl font-serif leading-none select-none rotate-180">&ldquo;</div>
      </div>

      <div className="max-w-6xl mx-auto px-4 md:px-6">
        {/* Header */}
        <div
          className="text-center mb-14 transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)" }}
        >
          <span className="inline-block text-amber-700 text-xs font-semibold uppercase tracking-widest mb-3">
            — Customer Love —
          </span>
          <h2
            className="text-4xl md:text-5xl font-bold text-stone-800 mb-4"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            {t("testi_title")}
          </h2>
          <p className="text-gray-600 text-lg">{t("testi_subtitle")}</p>
        </div>

        {/* Featured Testimonial */}
        <div
          className="relative transition-all duration-700"
          style={{ opacity: visible ? 1 : 0, transform: visible ? "translateY(0)" : "translateY(30px)", transitionDelay: "0.2s" }}
        >
          <div className="bg-gradient-to-br from-amber-50 to-[#f5ede0] rounded-3xl p-8 md:p-12 border border-amber-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 rounded-full opacity-10 pointer-events-none"
              style={{ background: "radial-gradient(circle, #f43f5e, transparent)", transform: "translate(30%, -30%)" }} />

            <div className="relative z-10">
              {/* Stars */}
              <div className="flex items-center gap-1 mb-6">
                {[...Array(5)].map((_, i) => (
                  <i key={i} className="ri-star-fill text-amber-400 text-xl"></i>
                ))}
              </div>

              {/* Quote */}
              <p
                className="text-gray-700 text-xl md:text-2xl leading-relaxed mb-8 italic transition-all duration-500"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                &ldquo;{isFr ? testimonials[active].textF : testimonials[active].text}&rdquo;
              </p>

              {/* Author */}
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-full overflow-hidden border-2 border-rose-200 flex-shrink-0">
                  <img
                    src={testimonials[active].image}
                    alt={testimonials[active].name}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div>
                  <p className="font-bold text-gray-800 text-base">{testimonials[active].name}</p>
                  <p className="text-amber-700 text-sm">{isFr ? testimonials[active].roleF : testimonials[active].role}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Dots */}
          <div className="flex items-center justify-center gap-2 mt-6">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                className={`rounded-full transition-all duration-300 cursor-pointer ${
                  i === active ? "w-8 h-2.5 bg-amber-600" : "w-2.5 h-2.5 bg-amber-200 hover:bg-amber-400"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Mini cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-10">
          {testimonials.map((testi, i) => (
            <button
              key={testi.id}
              onClick={() => setActive(i)}
              className={`p-4 rounded-2xl border text-left transition-all duration-300 cursor-pointer ${
                i === active
                  ? "border-amber-400 bg-amber-50 scale-[1.02]"
                  : "border-gray-100 bg-white hover:border-amber-200 hover:bg-amber-50/50"
              }`}
              style={{
                opacity: visible ? 1 : 0,
                transform: visible ? "translateY(0)" : "translateY(20px)",
                transition: `all 0.5s ease`,
                transitionDelay: `${0.4 + i * 0.1}s`,
              }}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                  <img src={testi.image} alt={testi.name} className="w-full h-full object-cover object-top" />
                </div>
                <div>
                  <p className="text-xs font-bold text-gray-800 truncate">{testi.name}</p>
                  <div className="flex items-center gap-0.5">
                    {[...Array(5)].map((_, j) => (
                      <i key={j} className="ri-star-fill text-amber-400 text-xs"></i>
                    ))}
                  </div>
                </div>
              </div>
              <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">
                {isFr ? testi.textF : testi.text}
              </p>
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}